import { useEffect } from "react";
import { Link } from "react-router-dom";
import NoDataAvailable from "@/components/NoDataAvailable/NoDataAvailable";
import Loader from "@/components/Loader/Loader";
import {
  BackArrowIcon,
  BreadCrumbChevronIcon,
  DownloadMiniIcon,
} from "@/components/SVGComponents/MemberIcons";
import {
  formatMembershipDate,
  formatPrice,
  formatStatusLabel,
} from "@/pages/Profile/Subscription/subscriptionFormatters";
import { PROFILE_BASE_PATH, PROFILE_CHILD_PATHS } from "@/pages/Profile/profilePaths";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchSubscriptionHistory } from "@/store/slices/subscriptionSlice";
import styles from "./BillingHistory.module.scss";

const subscriptionPath = `${PROFILE_BASE_PATH}/${PROFILE_CHILD_PATHS.SUBSCRIPTION}`;

const BillingHistory = () => {
  const dispatch = useAppDispatch();
  const { subscriptionHistory, isLoadingHistory, historyError } = useAppSelector(
    (state) => state.subscription,
  );

  useEffect(() => {
    dispatch(fetchSubscriptionHistory({}));
  }, [dispatch]);

  const handleDownloadInvoice = (invoiceUrl?: string | null) => {
    if (!invoiceUrl) return;
    window.open(invoiceUrl, "_blank", "noopener,noreferrer");
  };

  const renderContent = () => {
    if (isLoadingHistory && subscriptionHistory.length === 0) {
      return (
        <div className={styles.loaderWrap}>
          <Loader fullscreen={false} size={96} />
        </div>
      );
    }

    if (historyError) {
      return (
        <div className={styles.errorMessage}>
          <p>{historyError}</p>
        </div>
      );
    }

    if (subscriptionHistory.length === 0) {
      return (
        <div className={styles.emptyStateWrap}>
          <NoDataAvailable
            description="Your past subscriptions and invoices will appear here once available."
            note="Download invoices for previous plans from this list."
            title="No billing history available"
          />
        </div>
      );
    }

    return (
      <div className={styles.historyList}>
        {subscriptionHistory.map((plan) => (
          <article
            className={styles.historyCard}
            key={`${plan.transactionId ?? plan.planId}-${plan.startDate}`}
          >
            <div className={styles.cardHeader}>
              <div className={styles.titleRow}>
                <h2>{plan.name}</h2>
                {plan.invoiceUrl ? (
                  <button
                    className={styles.invoiceButton}
                    onClick={() => handleDownloadInvoice(plan.invoiceUrl)}
                    type="button"
                  >
                    <DownloadMiniIcon />
                    <span>Download invoice</span>
                  </button>
                ) : null}
              </div>
              <strong className={styles.price}>₹ {formatPrice(plan.amount)}</strong>
            </div>

            <div className={styles.metaGroup}>
              <div>
                <span>Start date</span>
                <strong>{formatMembershipDate(plan.startDate)}</strong>
              </div>
              <div>
                <span>End date</span>
                <strong>{formatMembershipDate(plan.endDate)}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong
                  className={`${styles.statusValue} ${
                    styles[plan.status?.toLowerCase() ?? ""]
                  }`}
                >
                  {formatStatusLabel(plan.status)}
                </strong>
              </div>
            </div>
          </article>
        ))}
      </div>
    );
  };

  return (
    <section className={styles.page}>
      <div className={styles.orbLeft} />
      <div className={styles.orbRight} />
      <div className={styles.dotsRight} />

      <div className={styles.inner}>
        <div className={styles.mobileHeader}>
          <Link aria-label="Back to membership" className={styles.backButton} to={subscriptionPath}>
            <BackArrowIcon />
          </Link>
          <h1>Billing History</h1>
          <span className={styles.headerSpacer} />
        </div>

        <div className={styles.breadcrumbs}>
          <Link className={styles.breadcrumbLink} to={PROFILE_BASE_PATH}>
            Profile
          </Link>
          <BreadCrumbChevronIcon />
          <Link className={styles.breadcrumbLink} to={subscriptionPath}>
            Membership
          </Link>
          <BreadCrumbChevronIcon />
          <span className={styles.activeCrumb}>Billing History</span>
        </div>

        {renderContent()}
      </div>
    </section>
  );
};

export default BillingHistory;
